import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { toast } from 'sonner';
import { Candidate } from '@/components/CandidateCard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { sendInvitationEmail } from '@/services/emailService';
import { encryptCandidateDetails } from '@/utils/encryption';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCw, Mail } from 'lucide-react';

// Import our components
import CandidatesList from '@/components/candidates/CandidatesList';
import CandidateHeader from '@/components/candidates/CandidateHeader';
import AssessmentRecording from '@/components/assessment/AssessmentRecording';
import AssessmentSummary from '@/components/candidates/AssessmentSummary';
import PerformanceComparison from '@/components/candidates/PerformanceComparison';
import SharingTools from '@/components/candidates/SharingTools';
import TranscriptSheet from '@/components/candidates/TranscriptSheet';
import ShareDialog from '@/components/candidates/ShareDialog';

const CandidateDetail: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState<any>(null);
  const [candidateAssessment, setCandidateAssessment] = useState<any>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [showCandidateList, setShowCandidateList] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [resendingInvitation, setResendingInvitation] = useState(false);

  useEffect(() => {
    if (!candidateId) {
      navigate('/candidates');
      return;
    }
    fetchCandidateDetails();
    fetchOtherCandidates();
  }, [candidateId, navigate]);

  const fetchCandidateDetails = async () => {
    if (!candidateId) return;

    try {
      setLoading(true);
      
      // Fetch candidate details
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select(`
          *,
          assessments (
            id,
            title,
            level,
            skills
          )
        `)
        .eq('id', candidateId)
        .single();

      if (candidateError) throw candidateError;
      if (!candidateData) throw new Error('Candidate not found');

      console.log('Candidate data:', {
        id: candidateData.id,
        assessment_id: candidateData.assessment_id,
        status: candidateData.status
      });

      // Fetch candidate assessment details if completed
      if (candidateData.status === 'completed') {
        console.log('Fetching assessment for:', {
          candidate_id: candidateId,
          assessment_id: candidateData.assessment_id
        });

        const { data: assessmentData, error: assessmentError } = await supabase
          .from('candidate_assessments')
          .select('*')
          .eq('candidate_id', candidateId)
          .eq('assessment_id', candidateData.assessment_id)
          .single();

        if (assessmentError) {
          console.error('Assessment fetch error:', assessmentError);
          throw assessmentError;
        }
        
        if (assessmentData) {
          // Get the public URL for the audio file
          const audioPath = `${candidateData.assessment_id}/${candidateId}/conversation.mp3`;
          const { data: { publicUrl } } = supabase.storage
            .from('assessment-recordings')
            .getPublicUrl(audioPath);

          // Add cache-busting parameter to ensure we get the latest file
          const cacheBuster = `?v=${Date.now()}`;
          const audioUrlWithCacheBusting = publicUrl + cacheBuster;

          console.log('Candidate assessment data:', {
            id: assessmentData.id,
            candidate_id: assessmentData.candidate_id,
            assessment_id: assessmentData.assessment_id,
            audio_url: audioUrlWithCacheBusting
          });

          setCandidateAssessment({
            ...assessmentData,
            audio_url: audioUrlWithCacheBusting
          });
        } else {
          console.log('No assessment data found');
        }
      }

      setCandidate({
        ...candidateData,
        assessmentTitle: candidateData.assessments.title
      });
    } catch (error: any) {
      console.error('Error fetching candidate details:', error);
      toast.error('Failed to load candidate details');
      navigate('/candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherCandidates = async () => {
    if (!candidateId) return;

    try {
      // First get the assessment ID for this candidate
      const { data: currentCandidate, error: candidateError } = await supabase
        .from('candidates')
        .select('assessment_id')
        .eq('id', candidateId)
        .single();

      if (candidateError) throw candidateError;

      // Then fetch all candidates for this assessment
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select(`
          *,
          assessments (
            title
          )
        `)
        .eq('assessment_id', currentCandidate.assessment_id)
        .order('created_at', { ascending: false });

      if (candidatesError) throw candidatesError;

      const formattedCandidates: Candidate[] = candidatesData.map(c => ({
        id: c.id,
        name: c.name,
        email: c.email,
        assessmentTitle: c.assessments.title,
        status: c.status as 'invited' | 'in_progress' | 'completed',
        completedDate: c.completed_date,
        score: c.score,
        aiRating: c.ai_rating,
        potentialPlagiarism: c.potential_plagiarism
      }));

      setCandidates(formattedCandidates);
    } catch (error: any) {
      console.error('Error fetching other candidates:', error);
      toast.error('Failed to load other candidates');
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleVolumeToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleCopyLink = () => {
    if (!candidateId) return;
    const publicUrl = `${window.location.origin}/candidate/${candidateId}`;
    navigator.clipboard.writeText(publicUrl);
    toast.success('Public link copied to clipboard');
  };

  const handleShareByEmail = () => {
    if (!shareEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (!shareEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    toast.success(`Assessment shared with ${shareEmail}`);
    setShareEmail('');
    setShowShareDialog(false);
  };

  const toggleCandidateList = () => {
    setShowCandidateList(!showCandidateList);
  };

  const handleSeek = (value: number[]) => {
    if (value.length > 0) {
      setCurrentTime(value[0]);
    }
  };

  const handleResendInvitation = async () => {
    if (!candidate || !candidate.assessment_id || !candidateId) return;
    
    try {
      setResendingInvitation(true);
      
      // Get assessment data including organization name
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessments')
        .select(`
          *,
          organizations:organization_id (
            name
          )
        `)
        .eq('id', candidate.assessment_id)
        .single();
      
      if (assessmentError) throw assessmentError;
      
      // Get sender info
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user?.id)
        .single();
      
      if (profileError && user) throw profileError;
      
      // Create sender name
      let senderName = 'RoundOne Team';
      if (profileData && user) {
        const firstName = profileData.first_name || '';
        const lastName = profileData.last_name || '';
        if (firstName || lastName) {
          senderName = `${firstName} ${lastName}`.trim();
        }
      }
      
      // Get organization name
      const organizationName = assessmentData?.organizations?.name || 'RoundOne AI';
      
      // Create assessment link token
      const token = encryptCandidateDetails({
        type: 'invite',
        assessmentId: candidate.assessment_id,
        candidateId: candidateId,
        name: candidate.name,
        email: candidate.email
      });
      
      // Send invitation email
      const emailSuccess = await sendInvitationEmail(
        [{
          recipientEmail: candidate.email,
          recipientName: candidate.name,
          assessmentLink: `${window.location.origin}/assessment/${candidate.assessment_id}/landing?token=${token}`,
          companyName: organizationName
        }],
        candidate.assessmentTitle,
        senderName,
        candidate.assessment_id
      );
      
      if (emailSuccess) {
        toast.success('Invitation resent successfully');
      } else {
        throw new Error('Failed to send invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast.error(error.message || 'Failed to resend invitation');
    } finally {
      setResendingInvitation(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="flex justify-center items-center h-full">
            <p>Loading candidate details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!candidate || !candidateId) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="flex justify-center items-center h-full">
            <p>Candidate not found</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto flex">
        {/* Candidate List Panel */}
        <CandidatesList 
          candidates={candidates} 
          currentCandidateId={candidateId} 
          showCandidateList={showCandidateList} 
          toggleCandidateList={toggleCandidateList}
        />
        
        {/* Candidate Detail Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <CandidateHeader 
              candidate={candidate} 
              showCandidateList={showCandidateList} 
              toggleCandidateList={toggleCandidateList}
            />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Display invitation status card when status is invited */}
                {candidate.status === 'invited' && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-center text-center space-y-2 flex-col py-8">
                        <Mail className="h-12 w-12 text-blue-500" />
                        <div className="space-y-1">
                          <h3 className="text-lg font-medium">Invitation Sent</h3>
                          <p className="text-sm text-muted-foreground">
                            Waiting for the candidate to start the assessment
                          </p>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={handleResendInvitation}
                            disabled={resendingInvitation}
                          >
                            {resendingInvitation ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Resending...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Resend Invitation
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Assessment Recording Card */}
                {candidateAssessment?.audio_url && (
                  <AssessmentRecording 
                    assessmentTitle={candidate.assessmentTitle}
                    audioUrl={candidateAssessment.audio_url}
                    isPlaying={isPlaying}
                    isMuted={isMuted}
                    currentTime={currentTime}
                    duration={duration}
                    handlePlayPause={handlePlayPause}
                    handleVolumeToggle={handleVolumeToggle}
                    handleOpenTranscript={() => setShowTranscript(true)}
                    handleSeek={handleSeek}
                    setDuration={setDuration}
                    setCurrentTime={setCurrentTime}
                  />
                )}
                
                {/* Assessment Summary Card */}
                {candidate.status === 'completed' && candidate.ai_rating && (
                  <AssessmentSummary 
                    summary={candidateAssessment?.transcript?.summary || "Assessment summary not available."}
                    aiRating={candidate.ai_rating}
                    skillRatings={candidateAssessment?.transcript?.skillRatings || {}}
                  />
                )}
              </div>
              
              <div className="space-y-6">
                {/* Performance Comparison Card */}
                {candidate.status === 'completed' && candidateAssessment?.transcript?.skillRatings && (
                  <PerformanceComparison 
                    skillRatings={candidateAssessment.transcript.skillRatings}
                    topCandidatesComparison={candidateAssessment.transcript.topCandidatesComparison || {}}
                  />
                )}
                
                {/* Share Assessment Card */}
                <SharingTools 
                  handleCopyLink={handleCopyLink}
                  handleShowShareDialog={() => setShowShareDialog(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Transcript Sheet */}
      {candidateAssessment?.transcript && (
        <TranscriptSheet 
          open={showTranscript}
          onOpenChange={setShowTranscript}
          assessmentTitle={candidate.assessmentTitle}
          transcript={candidateAssessment.transcript.content || "Transcript not available."}
        />
      )}
      
      {/* Share Dialog */}
      <ShareDialog 
        showShareDialog={showShareDialog}
        setShowShareDialog={setShowShareDialog}
        shareEmail={shareEmail}
        setShareEmail={setShareEmail}
        handleShareByEmail={handleShareByEmail}
      />
    </div>
  );
};

export default CandidateDetail;
